import { useState } from "react";

import siteConfig from "site-config";
import { fetchJSON } from "lib/fetch";

import { TAPIResponse } from "../types";
import { API_STATES } from "../helpers";

type TPlaceAutocompleteAddress = {
    addressLine1: string;
    city: string;
    state: string;
    zip: string;
    countryCode: string;
    formattedAddress: string;
};

export type TPlaceAutocompleteResponse = {
    places: TPlaceAutocompleteAddress[];
};

type TPlaceAutocompleteHookParams = {};

type TPlaceAutocompleteHookResponse<TPlaceAutocompleteParams, Response> = {
    (params?: TPlaceAutocompleteParams): Response & {
        fetch: (params: { address: string; countryCode: string }) => void;
    };
};

export const useFetchPlaceAutocomplete: TPlaceAutocompleteHookResponse<
    TPlaceAutocompleteHookParams,
    TAPIResponse<TPlaceAutocompleteResponse, string>
> = (params) => {
    const [state, setState] = useState<TAPIResponse<TPlaceAutocompleteResponse, string>>(API_STATES.default);
    const APIStates = API_STATES.getStates(setState);
    /*
     * Provide functionality to find an address
     * @param {string} address
     * @param {string} countryCode - "CA", "DE" etc. where CA is Canada and DE is Germany
     *
     * @see {@link https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes} List of ISO 3166-1 A-2 country codes
     */
    const fetch = (params: { address: string; countryCode?: string }) => {
        let URL = `${siteConfig.LOCATION_HUB_API_ROOT}/v2/place/autocomplete?input=${encodeURIComponent(params.address)}`;

        if (params.countryCode) {
            URL = `${URL}&countryCodes=${encodeURIComponent(params.countryCode)}`;
        }

        APIStates.pending();
        fetchJSON<TPlaceAutocompleteResponse>(URL, {
            method: "GET",
        })
            .then(APIStates.success)
            .catch((error) => {
                if (error.code === 401) {
                    error.message = "Unauthorized";
                }
                APIStates.error(error);
            });
    };

    return {
        data: state.data,
        error: state.error,
        isLoading: state.isLoading,
        loaded: state.loaded,
        fetch,
    };
};
